import {
  ACTION,
  Box,
  Button,
  ButtonVariants,
  DialogFooterWrapper,
  Divider,
  Flex,
  FormGroup,
  Icon,
  IconName,
  Input,
  MixpanelEvent,
  Modal,
  PopperHeader,
  Stack,
  Text,
  ToggleButton,
  ToggleGroup,
  useMixpanel,
  VStack,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { snakeCase } from 'lodash-es';
import type { FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useRoleAuth } from '../../../../hooks/useRoleAuth';
import { useUser } from '../../../../hooks/useUser';
import { useAppStateSelector } from '../../../../providers/AppStateProvider';
import { useUsersContext } from '../../../../providers/UsersContext';
import type { LayoutSection } from '../../../../types/LayoutConfig';
import { selectCurrentLayout, selectLayoutOptions } from '../../../AppLayout/AppLayoutSlice';
import { LayoutSectionBreadcrumb } from '../../../AppLayout/LayoutSectionBreadcrumb';
import type {
  LayoutSpecResponse,
  LayoutSpecShareResponse,
  LayoutSpecShareTarget,
  SpaceVisibility,
} from '../../../AppLayout/LayoutSpecs/LayoutSpecSlice';
import {
  useCreateLayoutSpecMutation,
  useDeleteLayoutSpecMutation,
  useLayoutSpecApiAccess,
  useLayoutSpecsQuery,
  useUpdateLayoutSpecMutation,
} from '../../../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { layoutSpecForSharing } from '../../../AppLayout/utils/layoutSpecForSharing';
import { AppLayoutPreview } from '../../../AppLayoutPreview/AppLayoutPreview';
import type { ShareType } from './ShareSelector';
import { ShareSelector } from './ShareSelector';

const AVAILABILITY_OPTION_TEMPLATE = {
  value: 'template' as const,
  displayName: 'Template only',
  tooltip: 'The template will only be available as a template. Users with access can add it as a space.',
};

const AVAILABILITY_OPTION_NEW_USERS = {
  value: 'new-users' as const,
  displayName: 'New users',
  tooltip: 'The template will be added as a space for new users, with access to the template.',
};

const AVAILABILITY_OPTION_ALL = {
  value: 'all' as const,
  displayName: 'All users',
  tooltip: 'The template will be added as a space for all users, with access to the template.',
};

export function ManageTemplateModal({
  layoutSection,
  initialLayoutId,
  selectedTemplateConfig,
  ...disclosure
}: {
  layoutSection: LayoutSection;
  initialLayoutId: string | undefined;
  selectedTemplateConfig: LayoutSpecResponse | undefined;
} & UseDisclosureReturn) {
  const layoutConfigs = useAppStateSelector(selectLayoutOptions);
  const currentLayout = useAppStateSelector(selectCurrentLayout);
  const storedLayoutSpec = initialLayoutId
    ? currentLayout?.id === initialLayoutId
      ? currentLayout
      : layoutConfigs?.[initialLayoutId]
    : undefined;
  const { canEditAllSpaces } = useLayoutSpecApiAccess();
  const { data: layoutSpecs = [] } = useLayoutSpecsQuery();
  const { isAuthorized } = useRoleAuth();
  const user = useUser();
  const { userByUserName } = useUsersContext();
  const [updateLayout, setUpdateLayout] = useState(false);
  const isEditing = selectedTemplateConfig != null;
  const ownerDisplayName =
    isEditing && selectedTemplateConfig.User !== user.Name
      ? (userByUserName.get(selectedTemplateConfig.User)?.Name ?? selectedTemplateConfig.User)
      : undefined;
  const layoutSpec =
    isEditing && !updateLayout
      ? selectedTemplateConfig.Config.layoutSpec
      : initialLayoutId
        ? storedLayoutSpec
        : undefined;

  const [newTemplateName, setNewTemplateName] = useState(
    isEditing
      ? selectedTemplateConfig?.Config.label
      : layoutSpec?.label
        ? `${layoutSpec?.label} (copy)`
        : 'New Template'
  );
  const [templateNameTouched, setTemplateNameTouched] = useState(false);
  const [availability, setAvailability] = useState<SpaceVisibility>(
    () => selectedTemplateConfig?.Config.availability ?? 'template'
  );
  const mixpanel = useMixpanel();
  const [createLayoutSpec, { isLoading: isCreatingTemplate }] = useCreateLayoutSpecMutation();
  const [updateLayoutSpec, { isLoading: isUpdatingTemplate }] = useUpdateLayoutSpecMutation();
  const [deleteLayoutSpec, { isLoading: isDeletingTemplate }] = useDeleteLayoutSpecMutation();
  const [shareType, setShareType] = useState<ShareType>(() => getInitialShareType(selectedTemplateConfig));
  const [shares, setShares] = useState<LayoutSpecShareTarget[]>(selectedTemplateConfig?.Shares ?? []);

  const canEditAllUsersDefaultSpace = isAuthorized(ACTION.EDIT_USERS_DEFAULT_SPACE);
  const canEditNewUsersDefaultSpace = isAuthorized(ACTION.EDIT_NEW_USERS_DEFAULT_SPACE);
  const canSetSpaceAvailability = canEditAllUsersDefaultSpace || canEditNewUsersDefaultSpace;
  const isPublic = shareType === 'public';
  const isSharedWithRolesOrGroups = shareType === 'roles' || shareType === 'userGroups';
  const isSharedWithUsers = shareType === 'users';
  const canEnableDefaultSpace = isPublic || isSharedWithRolesOrGroups || isSharedWithUsers;

  const availabilityOptions = useMemo(
    () => [
      AVAILABILITY_OPTION_TEMPLATE,
      ...(canEditNewUsersDefaultSpace ? [{ ...AVAILABILITY_OPTION_NEW_USERS, disabled: isSharedWithUsers }] : []),
      ...(canEditAllUsersDefaultSpace ? [AVAILABILITY_OPTION_ALL] : []),
    ],
    [canEditNewUsersDefaultSpace, canEditAllUsersDefaultSpace, isSharedWithUsers]
  );

  const validateTemplateName = useCallback(() => {
    setTemplateNameTouched(true);
  }, []);

  const existingTemplateNamesInSection = useMemo(() => {
    return layoutSpecs
      .filter(spec => spec.Config.section === layoutSection)
      .filter(spec => !isEditing || spec.ConfigID !== selectedTemplateConfig?.ConfigID)
      .map(spec => spec.Config.label);
  }, [layoutSpecs, layoutSection, isEditing, selectedTemplateConfig?.ConfigID]);

  const isDuplicateName = useMemo(() => {
    const trimmed = newTemplateName.trim();
    return (
      trimmed !== '' &&
      existingTemplateNamesInSection.some(
        name => name.trim().localeCompare(trimmed, undefined, { sensitivity: 'accent' }) === 0
      )
    );
  }, [newTemplateName, existingTemplateNamesInSection]);

  const templateNameValidationMessage = useMemo(
    () =>
      newTemplateName === ''
        ? 'Name cannot be empty'
        : isDuplicateName
          ? 'A template with this name already exists.'
          : undefined,
    [newTemplateName, isDuplicateName]
  );

  const copyOfLayoutSpec = useMemo(() => (layoutSpec ? structuredClone(layoutSpec) : undefined), [layoutSpec]);

  const handleUpdateTemplate = useCallback(
    (e: FormEvent<HTMLFormElement> & FormEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (selectedTemplateConfig == null) {
        return;
      }

      mixpanel.track(MixpanelEvent.UpdateLayoutTemplate, {
        section: layoutSection,
        template: newTemplateName,
        availability,
        shareType,
      });
      updateLayoutSpec({
        configID: selectedTemplateConfig.ConfigID,
        config: {
          ...selectedTemplateConfig.Config,
          label: newTemplateName,
          layoutSpec: layoutSpec
            ? layoutSpecForSharing({
                ...layoutSpec,
                deviceType: undefined,
                label: newTemplateName,
                id: selectedTemplateConfig.Config.layoutSpec.id,
                templateId: selectedTemplateConfig.Config.layoutSpec.templateId,
              })
            : selectedTemplateConfig.Config.layoutSpec,
          availability: canEnableDefaultSpace ? availability : undefined,
        },
        public: shareType === 'public',
        shares,
        useSpacesAdminApi: canEditAllSpaces,
      }).then(() => {
        disclosure.close();
      });
    },
    [
      disclosure,
      mixpanel,
      updateLayoutSpec,
      newTemplateName,
      selectedTemplateConfig,
      layoutSection,
      shareType,
      shares,
      availability,
      canEnableDefaultSpace,
      layoutSpec,
      canEditAllSpaces,
    ]
  );

  const handleCreateTemplate = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (copyOfLayoutSpec == null) {
        return;
      }
      const templateId = `${snakeCase(newTemplateName)}_${uuid()}`;
      mixpanel.track(MixpanelEvent.CreateNewLayoutTemplate, {
        section: layoutSection,
        template: newTemplateName,
        availability,
        shareType,
      });
      createLayoutSpec({
        config: {
          label: newTemplateName,
          id: templateId,
          section: layoutSection,
          layoutSpec: layoutSpecForSharing({
            ...copyOfLayoutSpec,
            label: newTemplateName,
            id: templateId,
            templateId,
          }),
          availability: canEnableDefaultSpace ? availability : undefined,
        },
        public: shareType === 'public',
        shares,
      }).then(() => {
        disclosure.close();
      });
    },
    [
      disclosure,
      mixpanel,
      createLayoutSpec,
      newTemplateName,
      copyOfLayoutSpec,
      shareType,
      shares,
      layoutSection,
      availability,
      canEnableDefaultSpace,
    ]
  );

  const handleShareTypeChange = useCallback((shareType: ShareType) => {
    setShareType(shareType);
    setShares([]);
  }, []);

  const handleDeleteTemplate = useCallback(() => {
    if (selectedTemplateConfig == null) {
      return;
    }
    mixpanel.track(MixpanelEvent.DeleteLayoutTemplate, {
      section: layoutSection,
      template: selectedTemplateConfig.Config.label,
    });
    deleteLayoutSpec({
      configID: selectedTemplateConfig.ConfigID,
      label: selectedTemplateConfig.Config.label,
      useSpacesAdminApi: canEditAllSpaces,
    }).then(() => {
      disclosure.close();
    });
  }, [canEditAllSpaces, disclosure, deleteLayoutSpec, selectedTemplateConfig, mixpanel, layoutSection]);

  return (
    <Modal {...disclosure} data-testid="create-template-modal" width="1100px" height="min(90vh, 800px)">
      <PopperHeader
        icon={IconName.Template}
        titlePrefix={<LayoutSectionBreadcrumb layoutSection={layoutSection} />}
        title={isEditing ? 'Editing Template' : 'Create Template'}
        onCloseClicked={disclosure.close}
      />
      <Flex
        as="form"
        id="manage-template-form"
        onSubmit={isEditing ? handleUpdateTemplate : handleCreateTemplate}
        overflow="hidden"
        flex="1 1 auto"
      >
        <Stack flex="0 0 auto" w="max(40%, 320px)" m="spacing16">
          <Box>
            <Box mb="spacing8">
              <Text as="h4" fontSize="fontSizeTitleH4" color="colorTextImportant" m={0}>
                {isEditing ? `Editing ${selectedTemplateConfig?.Config.label} Template` : 'Create Template'}
              </Text>
              {ownerDisplayName != null && (
                <Box fontSize="fontSizeDefault" color="colorTextSubtle">
                  <Icon icon={IconName.User} /> {ownerDisplayName}
                </Box>
              )}
            </Box>
            <Text fontSize="fontSizeDefault">
              {isEditing
                ? 'Update the template and its settings. Share it with other users in your organization. '
                : 'Create a new template from the current layout and share it with other users in your organization. '}
              A template can be added as a space for users with access to the template.
            </Text>
          </Box>
          <Divider my="spacing16" color="backgroundDivider" />
          <FormGroup
            label={isEditing ? 'Template name' : 'New template name'}
            error={templateNameTouched ? templateNameValidationMessage : undefined}
          >
            <Input
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
              onBlur={() => {
                setTemplateNameTouched(true);
              }}
              placeholder="Enter a name for the new template"
              invalid={templateNameValidationMessage != null}
              touched={templateNameTouched}
              data-testid="new-template-name-input"
            />
          </FormGroup>
          {canSetSpaceAvailability && (
            <FormGroup label="Add space by default">
              <ToggleGroup
                disabled={!canEnableDefaultSpace}
                defaultSelectedVariant={ButtonVariants.Priority}
                value={availability}
                options={availabilityOptions}
                onChange={setAvailability}
                data-testid="space-availability-toggle"
              />
            </FormGroup>
          )}
          <ShareSelector
            shareType={shareType}
            onShareTypeChange={handleShareTypeChange}
            shares={shares}
            onSharesChange={setShares}
          />
        </Stack>
        <VStack
          p="spacing48"
          gap="spacing16"
          background="backgroundContent"
          w="60%"
          flex="1 1 auto"
          position="relative"
        >
          {layoutSpec && (
            <>
              <Box maxWidth="100%">
                <Box mb="spacing8">{updateLayout || !isEditing ? 'Current layout' : 'Template layout'}</Box>
                <AppLayoutPreview key={updateLayout ? 'current' : 'template'} layoutSpec={layoutSpec} />
              </Box>
              {isEditing && layoutSpec && (
                <Box position="absolute" bottom="spacing16">
                  <ToggleButton
                    selected={!updateLayout}
                    onClick={() => setUpdateLayout(prev => !prev)}
                    selectedVariant={ButtonVariants.Primary}
                    data-testid={updateLayout ? 'use-current-layout-button' : 'reset-to-template-layout-button'}
                  >
                    {updateLayout ? 'Reset to template' : 'Update layout'}
                  </ToggleButton>
                </Box>
              )}
            </>
          )}
        </VStack>
      </Flex>
      <DialogFooterWrapper>
        {isEditing && (
          <Box mr="auto">
            <Button
              variant={ButtonVariants.Negative}
              onClick={handleDeleteTemplate}
              disabled={isDeletingTemplate}
              loading={isDeletingTemplate}
            >
              Delete Template
            </Button>
          </Box>
        )}
        <Button onClick={disclosure.close}>Cancel</Button>
        <Box onMouseEnter={validateTemplateName}>
          <Button
            type="submit"
            form="manage-template-form"
            variant={isEditing ? ButtonVariants.Primary : ButtonVariants.Positive}
            disabled={
              templateNameValidationMessage != null || layoutSpec == null || isCreatingTemplate || isUpdatingTemplate
            }
            loading={isCreatingTemplate || isUpdatingTemplate}
          >
            {isEditing ? 'Update Template' : 'Create New Template'}
          </Button>
        </Box>
      </DialogFooterWrapper>
    </Modal>
  );
}

function getInitialShareType(selectedTemplateConfig: LayoutSpecResponse | undefined): ShareType {
  if (selectedTemplateConfig == null || selectedTemplateConfig.IsPublic) {
    return 'public';
  }

  const shares = selectedTemplateConfig.Shares ?? [];
  if (shares.length === 0) {
    return 'private';
  }

  // Determine share type based on the first share's type
  const firstShare = shares[0] as LayoutSpecShareResponse;
  if ('Role' in firstShare && firstShare.Role != null) {
    return 'roles';
  }
  if ('GroupID' in firstShare && firstShare.GroupID != null) {
    return 'userGroups';
  }
  if ('User' in firstShare && firstShare.User != null) {
    return 'users';
  }

  return 'private';
}
